import React, { Fragment, useState } from "react";
import { BhiDialog } from "src/UIKit";
import DialogContent from "@mui/material/DialogContent";
import { Alert, Box, CircularProgress, LinearProgress, Stack, Typography } from "@mui/material";
import { LoadingOverlay } from "src/UIKit";
import UploadScreen from "./UploadScreen";
import MappingScreen from "./MappingScreen";
import ReviewScreen from "./ReviewScreen";
import StepIndicator from "./StepIndicator";
import { ImportContactsModalStep } from "./ImportContactsModalStep";
import AddContactsToGroups from "./AddContactsToGroups";
import { ContactColumns, ImportedContactsCsvSummary } from "src/modules/contacts/types";
import { useContactsApi } from "src/modules/contacts/api";
import { getCurrentCompanyId } from "src/modules/companies/slices";
import { useSelector } from "react-redux";
import { normalizeContactImportColumns } from "./helper";

type DownloadCsvModalType = {
  show: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  refreshGroups: () => void;
};

export function DownloadCsvModal({
  show,
  setShowModal,
  refreshGroups,
}: DownloadCsvModalType) {
  const defaultImportErrorMessage = 'Something went wrong while importing contacts. Please try again.';
  const [error, setError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [modalTitle, setModalTitle] = useState<string>('Import Contacts');
  const [createdContactsIds, setCreatedContactsIds] = useState<string[]>([]);
  const [existedContactsIds, setExistedContactsIds] = useState<string[]>([]);
  const [importContactsStep, setImportContactsStep] =
    useState<ImportContactsModalStep>(ImportContactsModalStep.upload);

  // New workflow state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [importSummary, setImportSummary] = useState<ImportedContactsCsvSummary | null>(null);
  const [isContactsUploading, setIsContactsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const { uploadContactsCsv, getImportContactsStatus } = useContactsApi();

  const handleClose = () => {
    if (isContactsUploading) {
      return;
    }
    setShowModal(false);
    refreshGroups();
  };

  const handleFileSelected = (file: File, headers: string[]) => {
    setError(false);
    setErrorMessage('');
    setUploadedFile(file);
    setCsvHeaders(headers);
    setImportContactsStep(ImportContactsModalStep.mapping);
  };

  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  const getImportErrorMessage = (err: unknown) => {
    if (typeof err === "string") return err;
    return defaultImportErrorMessage;
  };

  const processingContacts = async (
    taskId: string
  ): Promise<ImportedContactsCsvSummary | undefined> => {
    let sleepDuration = 200;
    let status = "processing";
    let progressValue = 20;
    do {
      const result = await getImportContactsStatus(selectedCompanyId!, taskId);
      status = result.data.status;

      if (status === "done") {
        if (result.data.outputType !== 'importCSV') {
          console.error("Unexpected outputType. It should be 'importCSV'.");
          throw defaultImportErrorMessage;
        }
        
        setUploadProgress(100);
        return result.data.output as ImportedContactsCsvSummary;
      }

      if (status === "failed") {
        const errorMessage = result.data.error ? result.data.error.message : defaultImportErrorMessage
        throw errorMessage;
      }
      
      progressValue = Math.min(progressValue + 3, 90);
      setUploadProgress(progressValue);
      
      await sleep(sleepDuration);
      sleepDuration += 50;
    } while (!["done", "failed"].includes(status));
  };

  const handleMappingComplete = async (mapping: ContactColumns, isAccepting: boolean) => {
    if (!uploadedFile || !selectedCompanyId || !isAccepting) return;

    setIsContactsUploading(true);
    setUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      formData.append("selectedColumns", JSON.stringify(normalizeContactImportColumns(mapping)));
      await sleep(1000);
      setUploadProgress(12);
      
      const response = await uploadContactsCsv(selectedCompanyId, formData);
      
      await sleep(800);
      setUploadProgress(15);
      
      const contacts = await processingContacts(response.data.taskId);

      if (contacts) {
        setImportSummary(contacts);
        setCreatedContactsIds(contacts.createdContacts || []);
        setExistedContactsIds(contacts.existedContacts || []);
        setImportContactsStep(ImportContactsModalStep.review);
      }
    } catch (e) {
      setError(true);
      console.error("Failed to import contacts CSV", e);
      setErrorMessage(getImportErrorMessage(e));
      setImportContactsStep(ImportContactsModalStep.upload);
    } finally {
      setIsContactsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleBackToUpload = () => {
    setUploadedFile(null);
    setCsvHeaders([]);
    setImportContactsStep(ImportContactsModalStep.upload);
  };

  const handleBackToMapping = () => {
    setImportContactsStep(ImportContactsModalStep.mapping);
  };

  const handleBackToReview = () => {
    setImportContactsStep(ImportContactsModalStep.review);
  };

  const handleAddToGroups = () => {
    setImportContactsStep(ImportContactsModalStep.addToGroups);
  };

  const handleSkip = () => {
    setShowModal(false);
    refreshGroups();
  };

  return (
    <Fragment>
      <BhiDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={show}
        fullWidth={true}
        keepMounted={false}
        title={modalTitle}
        closeButtonDisabled={isContactsUploading}
      >
        <DialogContent>
          <StepIndicator
            currentStep={importContactsStep}
          />
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage || "An error occurred while importing contacts. Please try again."}
            </Alert>
          )}
          <Stack alignItems="center">
            {importContactsStep === ImportContactsModalStep.upload && (
              <UploadScreen 
                onFileSelected={handleFileSelected}
                setIsUploading={setIsContactsUploading}
                setUploadProgress={setUploadProgress}
              />
            )}
            {importContactsStep === ImportContactsModalStep.mapping && (
              <MappingScreen
                csvHeaders={csvHeaders}
                onContinue={handleMappingComplete}
                isUploading={isContactsUploading}
                onBack={handleBackToUpload}
              />
            )}
            {importContactsStep === ImportContactsModalStep.review && importSummary && (
              <ReviewScreen
                summary={importSummary}
                onAddToGroups={handleAddToGroups}
                onSkip={handleSkip}
                onBack={handleBackToMapping}
              />
            )}
            {importContactsStep === ImportContactsModalStep.addToGroups && (
              <AddContactsToGroups
                error={error}
                setError={setError}
                existedContactsIds={existedContactsIds}
                createdContactsIds={createdContactsIds}
                setImportContactsStep={setImportContactsStep}
                handleClose={handleClose}
                setModalTitle={setModalTitle}
                onBack={handleBackToReview}
              />
            )}
          </Stack>
          {isContactsUploading && (
            <LoadingOverlay>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography variant="h6" fontWeight="fontWeightMedium" mb={2}>
                {importContactsStep === ImportContactsModalStep.upload 
                  ? "Reading File" 
                  : "Importing Contacts"}
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" mb={3} px={4}>
                {importContactsStep === ImportContactsModalStep.upload
                  ? "Please wait while we read and process your file. This may take a few moments depending on the file size."
                  : "Please wait while we process your contacts. This may take a few moments depending on the file size."}
              </Typography>
              <Box width="60%" height={8} mb={1} borderRadius={1} overflow="hidden">
                <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 1 }} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {uploadProgress}% complete
              </Typography>
            </LoadingOverlay>
          )}
        </DialogContent>
      </BhiDialog>
    </Fragment>
  );
}
